// frontend/src/hooks/chat/useChatMutations.ts
import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useMutationState,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  sendChatMessage,
  setChatMessageReaction,
  editChatMessage,
  deleteChatMessage,
  markChatChannelRead,
  startChatConversation,
} from '../../services/api';
import type { ChannelHeader, InboxRow, MessageOut } from '../../services/api';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useStore } from '../../store';
import { selectVisibleChannelIds } from '../../store/slices/chatSlice';
import { decodeMentions } from '../../components/chat/mentions';
import {
  chatKeys,
  mergeArrivals,
  refetchInboxCoalesced,
  sendOnlySet,
  type MessagePages,
} from './useChatQueries';

type SendVariables = {
  channelId: string;
  body: string;
  client_id: string;
  /** Kept on the mutation, so a Retry resends the same files. */
  attachment_ids: string[];
};

/** A send the server has not confirmed yet, or one that failed. */
export interface PendingSend {
  client_id: string;
  body: string;
  status: 'pending' | 'failed';
  attachment_ids: string[];
}

/**
 * The one send path, used by the Channel view and the New message view. The
 * mutation cache is global to the QueryClient, so a pending row survives the
 * New message → Channel switch, and the mutation-level `onSuccess` still runs
 * after the view that started the send unmounts.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationKey: chatKeys.send(),
    mutationFn: async ({
      channelId,
      body,
      client_id,
      attachment_ids,
    }: SendVariables) => {
      const { data } = await sendChatMessage({
        path: { channel_id: channelId },
        body: { body, client_id, attachment_ids },
        throwOnError: true,
      });
      return data as MessageOut;
    },
    onSuccess: (message, { channelId }) => {
      const cached = queryClient.getQueryData(
        chatKeys.messages(channelId)
      ) as MessagePages;
      if (!cached?.pages.some(page => page.some(m => m.seq === message.seq))) {
        sendOnlySet(queryClient, channelId).add(message.seq);
      }
      mergeArrivals(queryClient, channelId, [message]);
      refetchInboxCoalesced(queryClient);
    },
    onError: error => console.error('Chat send failed', error),
  });
  const send = useCallback(
    // One id per attempt, reused on retry: the server's unique index on
    // (channel, sender, client_id) makes the retry return the first row.
    (
      channelId: string,
      body: string,
      clientId?: string,
      attachmentIds: string[] = []
    ) => {
      const client_id = clientId ?? crypto.randomUUID();
      return mutateAsync({
        channelId,
        body,
        client_id,
        attachment_ids: attachmentIds,
      }).catch((error: unknown) => {
        // 403 (announcers only), 409 (archived) and 422 (a file the server will
        // not link) are permanent: a Retry can only fail again, so the failed
        // row is dropped. Network and 5xx failures keep it, with Retry.
        const status = getHttpStatus(error);
        if (status === 403 || status === 409 || status === 422) {
          // Keep the text as the channel's draft unless a new one was typed
          // meanwhile. Here, not in a view, so a send started from New
          // message (which has unmounted) keeps it too.
          const store = useStore.getState();
          if (!store.chatDrafts[channelId]) {
            // The draft shows `@Full Name`, and the picks come back from the
            // tokens, so a retry encodes them again.
            const { text, picks } = decodeMentions(body);
            store.setChatDraft(channelId, text);
            store.setChatDraftMentions(channelId, picks);
          }
          // `can_write` and `archived` live on the header, so the header is
          // what must refetch to show the locked or archived notice. `all`:
          // from New message the Channel view may not have subscribed yet, and
          // its first header load, still in flight, would answer can_write.
          void queryClient.invalidateQueries({
            queryKey: chatKeys.channel(channelId),
            refetchType: 'all',
          });
          const cache = queryClient.getMutationCache();
          cache
            .findAll({
              mutationKey: chatKeys.send(),
              predicate: m =>
                (m.state.variables as SendVariables | undefined)?.client_id ===
                client_id,
            })
            .forEach(m => cache.remove(m));
        }
        throw error;
      });
    },
    [mutateAsync, queryClient]
  );
  return { send };
}

interface ReactionVariables {
  messageId: string;
  emoji: string;
  reacted: boolean;
}

/**
 * Add or remove the caller's reaction. Not optimistic: on success the returned
 * list goes on the message, and the actor's own `chat.reaction` frame then
 * refetches the history, so a refetch that was already running cannot leave old
 * data. `pendingMessageIds` lets the UI ignore a click while a write for that
 * message is in flight.
 */
export function useSetReaction(channelId: string) {
  const queryClient = useQueryClient();
  const showError = useStore(state => state.showError);
  const { mutate } = useMutation({
    mutationKey: chatKeys.reaction(channelId),
    mutationFn: async ({ messageId, emoji, reacted }: ReactionVariables) => {
      const { data } = await setChatMessageReaction({
        path: { channel_id: channelId, message_id: messageId },
        body: { emoji, reacted },
        throwOnError: true,
      });
      return data as NonNullable<MessageOut['reactions']>;
    },
    onMutate: () => ({
      historyUpdatedAt:
        queryClient.getQueryState(chatKeys.messages(channelId))
          ?.dataUpdatedAt ?? 0,
    }),
    onSuccess: (reactions, { messageId }, started) => {
      // A history fetch landed while this write was in flight, and it can hold
      // a colleague's newer reaction. Keep it: the actor's own frame refetches.
      const historyUpdatedAt =
        queryClient.getQueryState(chatKeys.messages(channelId))
          ?.dataUpdatedAt ?? 0;
      if (historyUpdatedAt !== started?.historyUpdatedAt) return;
      queryClient.setQueryData(
        chatKeys.messages(channelId),
        (old: MessagePages) =>
          old && {
            ...old,
            pages: old.pages.map(page =>
              page.map(m => (m.id === messageId ? { ...m, reactions } : m))
            ),
          }
      );
    },
    onError: error => {
      console.error('Chat reaction failed', error);
      showError('Could not update the reaction');
      // Archived: the header is what shows it, as after a failed send.
      if (getHttpStatus(error) === 409) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.channel(channelId),
          refetchType: 'all',
        });
      }
    },
  });
  const setReaction = useCallback(
    (messageId: string, emoji: string, reacted: boolean) =>
      mutate({ messageId, emoji, reacted }),
    [mutate]
  );
  const pendingMessageIds: ReadonlySet<string> = new Set(
    useMutationState({
      filters: { mutationKey: chatKeys.reaction(channelId), status: 'pending' },
      select: m => (m.state.variables as ReactionVariables).messageId,
    })
  );
  return { setReaction, pendingMessageIds };
}

type ChangeVariables = { messageId: string; body?: string };

/**
 * Edit or delete one of your own messages. Not optimistic: on success the
 * returned row is written over its cached copy, then the history is
 * invalidated and refetched before the promise resolves. That write closes the
 * editor on the new text even when the actor's own frame cancels the refetch,
 * and the invalidation cancels a fetch already running, so no older page wins.
 * An edit is rare, so the extra history request is cheaper than a guard.
 */
export function useChangeMessage(channelId: string) {
  const queryClient = useQueryClient();
  const showError = useStore(state => state.showError);
  const { mutateAsync } = useMutation({
    mutationFn: async ({ messageId, body }: ChangeVariables) => {
      const { data } =
        body === undefined
          ? await deleteChatMessage({
              path: { channel_id: channelId, message_id: messageId },
              throwOnError: true,
            })
          : await editChatMessage({
              path: { channel_id: channelId, message_id: messageId },
              body: { body },
              throwOnError: true,
            });
      return data as MessageOut;
    },
    onSuccess: (message: MessageOut) => {
      queryClient.setQueryData(
        chatKeys.messages(channelId),
        (old: MessagePages) =>
          old && {
            ...old,
            pages: old.pages.map(page =>
              page.map(m => (m.id === message.id ? message : m))
            ),
          }
      );
      return queryClient.invalidateQueries({
        queryKey: chatKeys.messages(channelId),
      });
    },
    onError: (error, { body }: ChangeVariables) => {
      console.error('Chat message change failed', error);
      showError(
        getBusinessErrorMessage(error) ??
          (body === undefined
            ? 'Could not delete the message'
            : 'Could not save the edit')
      );
      // Archived: the header is what shows it, as after a failed reaction.
      if (getHttpStatus(error) === 409) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.channel(channelId),
          refetchType: 'all',
        });
      }
    },
  });
  const editMessage = useCallback(
    (messageId: string, body: string) =>
      mutateAsync({ messageId, body }).then(() => undefined),
    [mutateAsync]
  );
  const deleteMessage = useCallback(
    (messageId: string) => mutateAsync({ messageId }).then(() => undefined),
    [mutateAsync]
  );
  return { editMessage, deleteMessage };
}

/**
 * Pending and failed sends for one channel, one row per client_id. The mutation
 * cache lists attempts oldest first, so a retry's state replaces the failed
 * attempt's, and a success removes the row (the server row is in the messages cache).
 */
export function usePendingSends(channelId: string): PendingSend[] {
  const attempts = useMutationState({
    filters: {
      mutationKey: chatKeys.send(),
      predicate: m =>
        (m.state.variables as SendVariables | undefined)?.channelId ===
        channelId,
    },
    select: m => ({
      status: m.state.status,
      variables: m.state.variables as SendVariables,
    }),
  });
  const byClientId = new Map<string, PendingSend>();
  for (const { status, variables } of attempts) {
    if (status === 'success') byClientId.delete(variables.client_id);
    else if (status === 'pending' || status === 'error')
      byClientId.set(variables.client_id, {
        client_id: variables.client_id,
        body: variables.body,
        status: status === 'error' ? 'failed' : 'pending',
        attachment_ids: variables.attachment_ids,
      });
  }
  return [...byClientId.values()];
}

type MarkReadThrottle = {
  lastAt: number;
  seq: number;
  timer?: ReturnType<typeof setTimeout>;
};
const markReadState = new WeakMap<QueryClient, Map<string, MarkReadThrottle>>();
const activeMarkReadUsers = new WeakMap<QueryClient, number>();

/**
 * The panel must still show this channel when the POST goes out, not only when
 * the call was made.
 *
 * Checked here rather than passed in by the caller: the throttle record is
 * shared by every caller for a channel, so a guard held on it is cleared by the
 * next call, and the two callers (the channel view's own effect and the live
 * catch-up) do overlap inside one window.
 */
function panelShowsChannel(channelId: string): boolean {
  return selectVisibleChannelIds(useStore.getState()).includes(channelId);
}

/**
 * Forward-only read cursor, at most one write a second per channel. A call inside
 * the window is not dropped: the newest seq is sent when the window ends, so an
 * open channel's unread stays 0 when a frame lands right after it opened.
 * A hidden tab does not mark read: the post is skipped and the seq is kept, so
 * the next call made while the tab is visible sends it. A panel that no longer
 * shows the channel is the same: the person is not reading it any more.
 */
export function useMarkRead() {
  const queryClient = useQueryClient();
  useEffect(() => {
    activeMarkReadUsers.set(
      queryClient,
      (activeMarkReadUsers.get(queryClient) ?? 0) + 1
    );
    return () => {
      const remaining = (activeMarkReadUsers.get(queryClient) ?? 1) - 1;
      if (remaining > 0) {
        activeMarkReadUsers.set(queryClient, remaining);
      } else {
        activeMarkReadUsers.delete(queryClient);
        const byChannel = markReadState.get(queryClient);
        if (byChannel) {
          for (const state of byChannel.values()) {
            if (state.timer) {
              clearTimeout(state.timer);
              state.timer = undefined;
            }
          }
        }
      }
    };
  }, [queryClient]);
  return useCallback(
    (channelId: string, lastReadSeq: number) => {
      const byChannel =
        markReadState.get(queryClient) ?? new Map<string, MarkReadThrottle>();
      markReadState.set(queryClient, byChannel);
      const s = byChannel.get(channelId) ?? { lastAt: 0, seq: 0 };
      byChannel.set(channelId, s);
      s.seq = Math.max(s.seq, lastReadSeq);

      const post = () => {
        s.timer = undefined;
        if ((activeMarkReadUsers.get(queryClient) ?? 0) === 0) return;
        if (document.visibilityState !== 'visible') return;
        if (!panelShowsChannel(channelId)) return;
        s.lastAt = Date.now();
        const seq = s.seq;
        void markChatChannelRead({
          path: { channel_id: channelId },
          body: { last_read_seq: seq },
          throwOnError: true,
        })
          .then(() => {
            queryClient.setQueryData(
              chatKeys.inbox(),
              (rows: InboxRow[] | undefined) =>
                (rows ?? []).map(r =>
                  r.channel_id === channelId
                    ? {
                        ...r,
                        unread_count: 0,
                        has_unread_mention: false,
                        last_read_seq: seq,
                      }
                    : r
                )
            );
            // The patch is cache-only and has no last_message. Refetch after
            // the POST committed, so the server's unread count is already 0.
            refetchInboxCoalesced(queryClient);
          })
          .catch(error => console.error('Chat mark-read failed', error));
      };

      const since = Date.now() - s.lastAt;
      if (since >= 1000) {
        post();
        return;
      }
      if (!s.timer) s.timer = setTimeout(post, 1000 - since);
    },
    [queryClient]
  );
}

export function useStartConversation() {
  const find = useCallback(async (userIds: string[]) => {
    try {
      const { data } = await startChatConversation({
        body: { user_ids: userIds, create: false },
        throwOnError: true,
      });
      return data as ChannelHeader;
    } catch (error) {
      // 404 is "this set has no conversation yet", which the view shows as its
      // empty state — not the errors-table 404.
      if (getHttpStatus(error) === 404) return null;
      throw error;
    }
  }, []);

  const create = useCallback(async (userIds: string[]) => {
    const { data } = await startChatConversation({
      body: { user_ids: userIds },
      throwOnError: true,
    });
    return data as ChannelHeader;
  }, []);

  return { find, create };
}
